import React, { useRef, useState, useEffect } from "react";
import { Upload, Image as ImageIcon, Film, X } from "lucide-react";
import axios from "axios";
import { api, formatError } from "../api";
import { toast } from "sonner";

/**
 * FileUploader — drag/drop OR click-to-upload, uploads direct from the browser
 * to Cloudinary (via a one-time signed request from our backend). The file
 * never touches our backend, which is the only way to bypass the 1MB
 * Kubernetes ingress limit that was killing phone photo uploads.
 *
 * Returns: calls onChange(secure_url, kind) with the Cloudinary URL.
 *
 * Props:
 *  - accept: "image" | "video" | "any" (default "image")
 *  - value: current URL (renders a preview)
 *  - onChange: (url, kind) => void
 *  - label: optional label
 */
export const FileUploader = ({ accept = "image", value, onChange, label, testid = "file-uploader" }) => {
  const inputRef = useRef(null);
  const [uploading, setUploading] = useState(false);
  const [progress, setProgress] = useState(0);

  const acceptAttr =
    accept === "video"
      ? "video/*"
      : accept === "any"
      ? "image/*,video/*"
      : "image/*";

  // Cloudinary's free tier allows up to 100MB per file; the SDK can stream
  // larger but we don't need that here. We still keep a sanity cap so the
  // browser doesn't grind on multi-GB selections.
  const MAX_UPLOAD_BYTES = 100 * 1024 * 1024;

  const handleFile = async (file) => {
    if (!file) return;
    if (file.size > MAX_UPLOAD_BYTES) {
      toast.error(
        `File is ${(file.size / (1024 * 1024)).toFixed(1)}MB — max is ${MAX_UPLOAD_BYTES / (1024 * 1024)}MB. Resize and try again.`
      );
      return;
    }
    const isVideoFile =
      accept === "video" || (file.type && file.type.startsWith("video/"));
    const resourceType = isVideoFile ? "video" : "image";

    setUploading(true);
    setProgress(0);
    try {
      // 1. Ask our backend for a short-lived Cloudinary signature.
      //    The API secret stays on the server.
      const { data: sig } = await api.get(
        `/admin/cloudinary/signature?resource_type=${resourceType}`
      );

      // 2. Build the multipart form and POST it directly to Cloudinary.
      //    Important: we use a *bare* axios instance (no withCredentials,
      //    no shared interceptors) because Cloudinary doesn't want our cookies.
      const form = new FormData();
      form.append("file", file);
      form.append("api_key", sig.api_key);
      form.append("timestamp", sig.timestamp);
      form.append("signature", sig.signature);
      form.append("folder", sig.folder);

      const cloudUrl = `https://api.cloudinary.com/v1_1/${sig.cloud_name}/${sig.resource_type}/upload`;
      const { data } = await axios.post(cloudUrl, form, {
        onUploadProgress: (e) =>
          setProgress(Math.round((e.loaded * 100) / (e.total || 1))),
      });

      const secureUrl = data?.secure_url;
      if (!secureUrl) {
        toast.error("Cloudinary didn't return a URL. Try again.");
        return;
      }
      onChange(secureUrl, sig.resource_type);
      toast.success("Uploaded.");
    } catch (e) {
      const status = e?.response?.status;
      // Cloudinary's error body lives under data.error.message; our backend
      // signature errors come back as data.detail.
      const cloudErr = e?.response?.data?.error?.message;
      const apiErr = formatError(e?.response?.data?.detail);
      if (status === 401 || status === 403) {
        toast.error("Session expired — log out, log back in, then retry the upload.");
      } else if (cloudErr) {
        toast.error(`Cloudinary rejected the upload: ${cloudErr}`);
      } else if (apiErr) {
        toast.error(apiErr);
      } else {
        toast.error(`Upload failed${status ? ` (${status})` : ""} — ${e?.message || "unknown error"}`);
      }
    } finally {
      setUploading(false);
    }
  };

  // Recognise both Cloudinary and our own legacy URLs as videos.
  const isVideo = value && /\.(mp4|mov|webm|m4v)(\?|$)/i.test(value);
  const fullUrl =
    value && value.startsWith("/api/")
      ? `${process.env.REACT_APP_BACKEND_URL}${value}`
      : value;
  const [broken, setBroken] = useState(false);
  /* eslint-disable */
  useEffect(() => { setBroken(false); }, [fullUrl]);
  /* eslint-enable */

  return (
    <div data-testid={testid}>
      {label && (
        <label className="block font-mono text-[10px] uppercase tracking-[0.25em] mb-1">{label}</label>
      )}
      <div className="flex items-stretch gap-3">
        {value ? (
          <div className="relative w-24 h-24 border-2 border-asphalt bg-bone shrink-0 overflow-hidden">
            {broken ? (
              <div className="w-full h-full flex flex-col items-center justify-center bg-card-light font-mono text-[8px] uppercase tracking-[0.2em] text-terra text-center px-1">
                Media not found
              </div>
            ) : isVideo ? (
              <video src={fullUrl} className="w-full h-full object-cover" muted onError={() => setBroken(true)} />
            ) : (
              <img src={fullUrl} alt="" className="w-full h-full object-cover" onError={() => setBroken(true)} />
            )}
            <button
              type="button"
              onClick={() => onChange("")}
              className="absolute top-0 right-0 bg-terra text-bone p-0.5"
              data-testid={`${testid}-clear`}
            >
              <X className="w-3 h-3" />
            </button>
          </div>
        ) : null}
        <div className="flex-1 flex flex-col gap-2">
          <button
            type="button"
            onClick={() => inputRef.current?.click()}
            disabled={uploading}
            data-testid={`${testid}-pick`}
            className="border-2 border-asphalt bg-bone px-3 py-2 font-mono text-[11px] uppercase tracking-[0.2em] hover:bg-asphalt hover:text-bone transition-colors flex items-center justify-center gap-2 disabled:opacity-60"
          >
            {accept === "video" ? <Film className="w-4 h-4" /> : <ImageIcon className="w-4 h-4" />}
            {uploading ? `Uploading… ${progress}%` : value ? "Replace" : "Upload from device"}
          </button>
          <input
            value={value || ""}
            onChange={(e) => onChange(e.target.value)}
            placeholder="…or paste an existing URL"
            className="border-2 border-asphalt bg-bone px-3 py-2 font-mono text-xs"
            data-testid={`${testid}-url`}
          />
        </div>
        <input
          ref={inputRef}
          type="file"
          accept={acceptAttr}
          className="hidden"
          onChange={(e) => handleFile(e.target.files?.[0])}
        />
      </div>
    </div>
  );
};

/**
 * GalleryUploader — multi-media picker. Maintains an array of URLs.
 *
 * Props:
 *  - value: string[]
 *  - onChange: (arr) => void
 *  - accept: "image" | "video" (default "image")
 */
export const GalleryUploader = ({ value = [], onChange, label, testid = "gallery-uploader", accept = "image" }) => {
  const add = (url) => {
    if (!url) return;
    onChange([...value, url]);
  };
  const remove = (i) => onChange(value.filter((_, idx) => idx !== i));
  const move = (i, dir) => {
    const next = [...value];
    const j = i + dir;
    if (j < 0 || j >= next.length) return;
    [next[i], next[j]] = [next[j], next[i]];
    onChange(next);
  };

  const isVideoUrl = (url) => /\.(mp4|mov|webm|m4v)(\?|$)/i.test(url);

  return (
    <div data-testid={testid}>
      {label && (
        <label className="block font-mono text-[10px] uppercase tracking-[0.25em] mb-1">{label}</label>
      )}
      <div className="grid grid-cols-3 sm:grid-cols-4 gap-2 mb-2">
        {value.map((url, i) => {
          const full = url.startsWith("/api/") ? `${process.env.REACT_APP_BACKEND_URL}${url}` : url;
          const showVideo = accept === "video" || isVideoUrl(url);
          return (
            <div key={url} className="relative aspect-square border-2 border-asphalt bg-bone overflow-hidden group">
              {showVideo ? (
                <video src={full} className="w-full h-full object-cover" muted />
              ) : (
                <img src={full} alt="" className="w-full h-full object-cover" />
              )}
              <div className="absolute inset-x-0 bottom-0 flex justify-between bg-asphalt/80 opacity-0 group-hover:opacity-100 transition-opacity">
                <button
                  type="button"
                  onClick={() => move(i, -1)}
                  className="text-bone px-2 py-0.5 font-mono text-[10px]"
                  data-testid={`${testid}-up-${i}`}
                  title="Move up"
                >
                  ←
                </button>
                <button
                  type="button"
                  onClick={() => move(i, 1)}
                  className="text-bone px-2 py-0.5 font-mono text-[10px]"
                  data-testid={`${testid}-down-${i}`}
                  title="Move down"
                >
                  →
                </button>
              </div>
              <button
                type="button"
                onClick={() => remove(i)}
                className="absolute top-0 right-0 bg-terra text-bone p-1"
                data-testid={`${testid}-remove-${i}`}
              >
                <X className="w-3 h-3" />
              </button>
            </div>
          );
        })}
      </div>
      <FileUploader accept={accept} value="" onChange={add} testid={`${testid}-add`} />
    </div>
  );
};
